import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import MinUidContext, { MinUidConsumer } from './MinUidContext'
import MaxUidContext, { MaxUidConsumer } from './MaxUidContext'
import MaxVideoView from './MaxVideoView'
import MinVideoView from './MinVideoView'
import PropsContext from './PropsContext'
import { VideoLayout } from './FittedVideo'
import useElementSize from './Utils/useElementSize'
import {
  DEFAULT_PREVIEW_RATIO,
  placePreviews,
  Rect,
  sameRect,
  sameSize,
  Size
} from './Utils/videoLayout'
import styles from './styles.module.css'

/**
 * React Component to render the user videos in the pinned/spotlight layout
 */
const PinnedVideo: React.FC = () => {
  const { styleProps } = useContext(PropsContext)
  return styleProps?.adaptivePinnedLayout ? (
    <AdaptivePinnedVideo />
  ) : (
    <ClassicPinnedVideo />
  )
}

/**
 * Pinned layout that follows the streams' shapes (styleProps.adaptivePinnedLayout).
 *
 * The big view fills the stage and FittedVideo draws the picture at the stream's own aspect
 * ratio inside it. The small views are positioned from that picture's rectangle with
 * `placePreviews`: in the free space beside or above it when there is room, otherwise over its
 * top-left corner. Everything is recomputed when the stage is resized or a stream changes shape
 * (e.g. the patient rotates their phone). Only positions and sizes change, so no tile is
 * remounted and no video restarts.
 */
const AdaptivePinnedVideo: React.FC = () => {
  const { styleProps, rtcProps } = useContext(PropsContext)
  const { minViewContainer, pinnedVideoContainer, maxViewContainer } =
    styleProps || {}
  const maxUsers = useContext(MaxUidContext)
  const minUsers = useContext(MinUidContext)
  const stageRef = useRef<HTMLDivElement>(null)
  const stage = useElementSize(stageRef)

  // Rectangle the main picture is drawn in (relative to the stage), when known.
  const [mainFrame, setMainFrame] = useState<Rect | null>(null)
  // Stream size of each small view, by uid.
  const [previewSources, setPreviewSources] = useState<Record<string, Size>>({})

  const onMainLayout = useCallback((layout: VideoLayout | null) => {
    const frame = layout ? layout.frame : null
    setMainFrame((prev) => (sameRect(prev, frame) ? prev : frame))
  }, [])

  const onPreviewLayout = useCallback(
    (uid: string, layout: VideoLayout | null) => {
      const source = layout ? layout.source : null
      setPreviewSources((prev) => {
        const current = prev[uid] || null
        if (sameSize(current, source)) return prev
        const next = { ...prev }
        if (source) next[uid] = source
        else delete next[uid]
        return next
      })
    },
    []
  )

  const mainUser = maxUsers[0]
  const showMain = !(rtcProps.role === 'audience' && mainUser.uid === 0)
  const previews = minUsers.filter(
    (user) => !(rtcProps.role === 'audience' && user.uid === 0)
  )

  // Forget sizes of views that are gone (a user left, a screen share stopped).
  const previewKeys = previews.map((user) => String(user.uid)).join(',')
  useEffect(() => {
    const present = new Set(previewKeys ? previewKeys.split(',') : [])
    setPreviewSources((prev) => {
      const stale = Object.keys(prev).filter((uid) => !present.has(uid))
      if (!stale.length) return prev
      const next = { ...prev }
      stale.forEach((uid) => delete next[uid])
      return next
    })
  }, [previewKeys])

  const ratios = previews.map((user) => {
    const source = previewSources[String(user.uid)]
    return source ? source.width / source.height : DEFAULT_PREVIEW_RATIO
  })
  const placement = stage
    ? placePreviews(stage, showMain ? mainFrame : null, ratios)
    : null

  return (
    <div
      ref={stageRef}
      style={{
        ...adaptiveStyles.stage,
        ...pinnedVideoContainer
      }}
    >
      <div
        style={{
          ...adaptiveStyles.main,
          ...maxViewContainer
        }}
      >
        {showMain && (
          <MaxVideoView user={mainUser} onVideoLayout={onMainLayout} />
        )}
      </div>
      {previews.map((user, index) => {
        const rect = placement ? placement.rects[index] : undefined
        return (
          <div
            key={user.uid}
            style={{
              ...adaptiveStyles.preview,
              ...minViewContainer,
              // Computed geometry always wins over the style props.
              position: 'absolute',
              left: rect ? rect.x : 0,
              top: rect ? rect.y : 0,
              width: rect ? rect.width : 0,
              height: rect ? rect.height : 0,
              visibility: rect ? 'visible' : 'hidden'
            }}
          >
            <MinVideoView
              user={user}
              onVideoLayout={(layout) =>
                onPreviewLayout(String(user.uid), layout)
              }
            />
          </div>
        )
      })}
    </div>
  )
}

const adaptiveStyles = {
  stage: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden'
  } as CSSProperties,
  main: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex'
  } as CSSProperties,
  preview: {
    display: 'flex',
    zIndex: 110
  } as CSSProperties
}

/**
 * The original pinned layout: big view plus a scrolling strip of small views.
 */
const ClassicPinnedVideo: React.FC = () => {
  const { styleProps, rtcProps } = useContext(PropsContext)
  const {
    minViewContainer,
    pinnedVideoContainer,
    maxViewContainer,
    scrollViewContainer
  } = styleProps || {}
  const parentRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const isLandscape = width > height

  useEffect(() => {
    const handleResize = () => {
      if (parentRef.current) {
        setWidth(parentRef.current.offsetWidth)
        setHeight(parentRef.current.offsetHeight)
      }
    }
    window.addEventListener('resize', handleResize)
    if (parentRef.current) {
      setWidth(parentRef.current.offsetWidth)
      setHeight(parentRef.current.offsetHeight)
    }
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <div
      ref={parentRef}
      style={{
        ...{
          display: 'flex',
          flex: 1,
          flexDirection: isLandscape ? 'row' : 'column-reverse',
          overflow: 'hidden'
        },
        ...pinnedVideoContainer
      }}
    >
      <div
        style={{
          ...{
            display: 'flex',
            flex: isLandscape ? 5 : 4
          },
          ...maxViewContainer
        }}
      >
        <MaxUidConsumer>
          {(maxUsers) =>
            rtcProps.role === 'audience' && maxUsers[0].uid === 0 ? null : (
              <MaxVideoView user={maxUsers[0]} />
            )
          }
        </MaxUidConsumer>
      </div>
      <div
        className={styles.scrollbar}
        style={{
          ...{
            overflowY: isLandscape ? 'scroll' : 'hidden',
            overflowX: !isLandscape ? 'scroll' : 'hidden',
            display: 'flex',
            flex: 1,
            flexDirection: isLandscape ? 'column' : 'row'
          },
          ...scrollViewContainer
        }}
      >
        <MinUidConsumer>
          {(minUsers) =>
            minUsers.map((user) =>
              rtcProps.role === 'audience' && user.uid === 0 ? null : (
                <div
                  style={{
                    ...{
                      minHeight: isLandscape ? '35vh' : '99%',
                      minWidth: isLandscape ? '99%' : '40vw',
                      margin: 2,
                      display: 'flex'
                    },
                    ...minViewContainer
                  }}
                  key={user.uid}
                >
                  <MinVideoView user={user} />
                </div>
              )
            )
          }
        </MinUidConsumer>
      </div>
    </div>
  )
}

export default PinnedVideo
